const MAX_ENTRIES = 200;
export { notificationCenterKey } from './persistedState.js';
/** Legacy chat completions and action approvals belong to chat, never the notifications feed. */
export function isCenterNotification(entry) {
    if (entry.actionId || /^(?:answer|action):/.test(entry.id))
        return false;
    if (entry.kind === 'monitoring')
        return entry.userRequested === true
            && typeof entry.monitorRequestId === 'string' && /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,119}$/.test(entry.monitorRequestId)
            && entry.id.startsWith(`monitor:${entry.monitorRequestId}:`) && entry.id.length > `monitor:${entry.monitorRequestId}:`.length
            && (entry.status === 'done' || entry.status === 'error') && (entry.source === 'gyro' || entry.source === 'exante');
    return entry.source === 'exante' && (entry.kind === undefined || entry.kind === 'organization');
}
export function visibleNotifications(entries) {
    return entries.filter(isCenterNotification);
}
export function normalizeNotifications(value) {
    if (!Array.isArray(value))
        return [];
    const entries = [];
    const seen = new Set();
    for (const item of value) {
        if (!item || typeof item !== 'object' || typeof item.id !== 'string' || !item.id || item.id.length > 240 || seen.has(item.id)
            || !['request', 'update', 'alert'].includes(item.category) || !['exante', 'gyro'].includes(item.source)
            || typeof item.title !== 'string' || typeof item.body !== 'string' || typeof item.status !== 'string'
            || !Number.isFinite(item.createdAt) || item.createdAt <= 0 || !Number.isFinite(item.updatedAt) || item.updatedAt <= 0)
            continue;
        if (!isCenterNotification(item))
            continue;
        seen.add(item.id);
        entries.push({
            id: item.id, category: item.category, source: item.source,
            title: item.title.slice(0, 200), body: item.body.slice(0, 700),
            createdAt: item.createdAt, updatedAt: item.updatedAt, read: item.read === true, status: item.status.slice(0, 60),
            kind: item.kind === 'monitoring' ? 'monitoring' : 'organization',
            ...(item.kind === 'monitoring' ? { monitorRequestId: item.monitorRequestId, userRequested: true } : {}),
            ...(typeof item.conversationId === 'string' ? { conversationId: item.conversationId.slice(0, 200) } : {}),
        });
    }
    return entries.sort((a, b) => b.updatedAt - a.updatedAt).slice(0, MAX_ENTRIES);
}
export function readNotificationCenter(key) {
    try {
        return normalizeNotifications(JSON.parse(localStorage.getItem(key) ?? '[]'));
    }
    catch {
        return [];
    }
}
export function upsertNotification(previous, entry) {
    if (!isCenterNotification(entry))
        return previous;
    const old = previous.find(item => item.id === entry.id);
    // Replayed incoming events must never reset the read state or notification time.
    if (old && old.status === entry.status && old.title === entry.title && old.body === entry.body) {
        return old.available === entry.available ? previous : previous.map(item => item.id === old.id ? { ...item, available: entry.available } : item);
    }
    return [{ ...entry, createdAt: old?.createdAt ?? entry.createdAt, read: false }, ...previous.filter(item => item.id !== entry.id)]
        .sort((a, b) => b.updatedAt - a.updatedAt).slice(0, MAX_ENTRIES);
}
/** Notification content from the same trusted host boundary as existing Gyro shell events. No actions or URLs. */
export function parseHostNotification(value) {
    const entries = normalizeNotifications([value]);
    const entry = entries[0];
    if (!entry || entry.actionId || entry.conversationId)
        return null;
    // The current native producer emits support events. A monitoring producer must explicitly
    // identify a user-requested check and its result; ordinary polling/chat events cannot qualify.
    if (entry.kind !== 'monitoring' && (!entry.id.startsWith('support:') || entry.source !== 'exante'))
        return null;
    return { ...entry, read: false };
}
export function markNotificationRead(previous, id, read = true) {
    let changed = false;
    const next = previous.map(entry => {
        if ((id !== null && entry.id !== id) || entry.read === read)
            return entry;
        changed = true;
        return { ...entry, read };
    });
    return changed ? next : previous;
}
//# sourceMappingURL=notificationCenter.js.map